import { conform, useForm, type Submission } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import { json, type DataFunctionArgs } from '@remix-run/node'
import { Form, useActionData, useSearchParams } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { getGmailUserId, login, sessionKey, signupWithOTP } from '#app/utils/auth.server.ts'
//import { validateCSRF } from '#app/utils/csrf.server.ts'
import { prisma } from '#app/utils/db.server.ts'
//import { checkHoneypot } from '#app/utils/honeypot.server.ts'
//import { ensurePrimary } from '#app/utils/litefs.server.ts'
import { getDomainUrl, getSourceDeviceType, useIsPending } from '#app/utils/misc.tsx'
import { resendMsg91OTP, sendMsg91EmailOTP, sendMsg91OTP, verifyMsg91OTP } from '#app/utils/otp.server.ts'
import { isAdminUserForRedirect } from '#app/utils/permissions.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { sendOTPDoneSlackMessage } from '#app/utils/slack.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { generateTOTP, verifyTOTP } from '#app/utils/totp.server.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import { handleVerification as handleOnboardingVerification } from './_authlayout.onboarding.tsx'

export const codeQueryParam = 'code'
export const targetQueryParam = 'target'
export const typeQueryParam = 'type'
export const redirectToQueryParam = 'redirectTo'
const types = ['onboarding', 'reset-password', 'change-email', '2fa', 'resend', 'verify-email'] as const
const VerificationTypeSchema = z.enum(types)
export type VerificationTypes = z.infer<typeof VerificationTypeSchema>

const VerifySchema = z.object({
	[codeQueryParam]: z.string().min(6).max(6),
	[typeQueryParam]: VerificationTypeSchema,
	[targetQueryParam]: z.string(),
	[redirectToQueryParam]: z.string().optional(),
})

export async function action({ request }: DataFunctionArgs) {
	const formData = await request.formData()
	// checkHoneypot(formData)

	const type = formData.get('type')
	const mobileNo = formData.get('target') as string
	const submission = parse(formData, {
		schema: VerifySchema
	})
	switch(type) {
		case 'onboarding' : {
	      return validateRequest(request, formData)
		}
		case 'resend' : {
          if(process.env.OTP_ENABLE === 'TRUE'){
			const { redirectTo, otpResult: response } = await prepareResendOtpVerification({ request, type: 'onboarding', target: mobileNo, fdRedirectTo: submission.payload?.[redirectToQueryParam]?.toString()})

				if (response.status === 'success') {
					// return redirect(redirectTo.toString())
					return redirectWithToast(
						redirectTo.toString(),
						{
							type: 'success',
							description: 'Successfully sent Resend OTP.'
						}
					)
				} else {
					submission.error[''] = ['Resend OTP not successfull.']
					return json({ status: 'error', submission } as const, { status: 500 })
				}
			}else{
				const { redirectTo } = await prepareVerification({
					period: 10 * 60,
					request,
					type: 'onboarding',
					target: mobileNo,
				})

				const response = {
					status: 'success',
					msg: 'otp sent successfully'
				}	
				
				if (response.status === 'success') {
				// return redirect(redirectTo.toString())
				return redirectWithToast(
					redirectTo.toString(),
					{
						type: 'success',
						description: 'Successfully sent Resend OTP.'
					}
					)
				} else {
					submission.error[''] = [response?.msg ?? 'Something Wrong, try again later.']
					return json({ status: 'error', submission } as const, { status: 500 })
				}
			}
          
		}
		default: {
			submission.error[''] = ['Something Wrong, try again later.'] as any
			return json({ status: 'error', submission } as const, { status: 500 })
		}
	}
}

export function getRedirectToUrl({
	request,
	type,
	target,
	redirectTo,
}: {
	request: Request
	type: VerificationTypes
	target: string
	redirectTo?: string
}) {
	const redirectToUrl = new URL(`${getDomainUrl(request)}/verify`)
	redirectToUrl.searchParams.set(typeQueryParam, type)
	redirectToUrl.searchParams.set(targetQueryParam, target)
	if (redirectTo) {
		redirectToUrl.searchParams.set(redirectToQueryParam, redirectTo)
	}
	return redirectToUrl
}

// export async function requireRecentVerification(request: Request) {
// 	const userId = await requireUserId(request)
// 	const shouldReverify = await shouldRequestTwoFA(request)
// 	if (shouldReverify) {
// 		const reqUrl = new URL(request.url)
// 		const redirectUrl = getRedirectToUrl({
// 			request,
// 			target: userId,
// 			type: twoFAVerificationType,
// 			redirectTo: reqUrl.pathname + reqUrl.search,
// 		})
// 		throw await redirectWithToast(redirectUrl.toString(), {
// 			title: 'Please Reverify',
// 			description: 'Please reverify your account before proceeding',
// 		})
// 	}
// }

export async function prepareVerification({
	period,
	request,
	type,
	target,
	fdRedirectTo
}: {
	period: number
	request: Request
	type: VerificationTypes
	target: string
	fdRedirectTo?: string
}) {
	const verifyUrl = getRedirectToUrl({ request, type, target, redirectTo: fdRedirectTo })
	const redirectTo = new URL(verifyUrl.toString())

	// SEND OTP TO MOBILE 

	let { otp, ...verificationConfig } = generateTOTP({
		algorithm: 'SHA256',
		charSet: '123456789',
		period,
	})
	otp = '123456'
	const verificationData = {
		type,
		target,
		...verificationConfig,
		expiresAt: new Date(Date.now() + verificationConfig.period * 1000),
	}

	await prisma.verification.upsert({
		where: { target_type: { target, type } },
		create: verificationData,
		update: verificationData,
	})

	// add the otp to the url we'll email the user.
	verifyUrl.searchParams.set(codeQueryParam, otp)

	return { otp, redirectTo, verifyUrl }
}

export async function prepareEmailVerification({
	period,
	request,
	type,
	target,
	fdRedirectTo
}: {
	period: number
	request: Request
	type: VerificationTypes
	target: string
	fdRedirectTo?: string
}) {
	const verifyUrl = getRedirectToUrl({ request, type, target, redirectTo: fdRedirectTo })
	const redirectTo = new URL(verifyUrl.toString())

	// generate OTP for email 

	let { otp, ...verificationConfig } = generateTOTP({
		algorithm: 'SHA256',
		charSet: '123456789',
		period,
	})

	const verificationData = {
		type,
		target,
		...verificationConfig,
		expiresAt: new Date(Date.now() + verificationConfig.period * 1000),
	}

	await prisma.verification.upsert({
		where: { target_type: { target, type } },
		create: verificationData,
		update: verificationData,
	})

	// send the email with otp
	const otpResult = await sendMsg91EmailOTP(target, otp)

	return { redirectTo, otpResult }
}

export async function prepareOtpVerification({
    request,
	type,
	target,
	fdRedirectTo
}: {
	request: Request,
	type: VerificationTypes,
	target: string, // should be mobile no.
	fdRedirectTo?: string
}) {
	const verifyUrl = getRedirectToUrl({ request, type, target, redirectTo: fdRedirectTo})
	const redirectTo = new URL(verifyUrl.toString())

	if(target === '9876543211'){
		// it's for admin
		const otpResult = { status: 'success', data: { success: true, data: { type: 'success', request_id: '346526'}}}
	
        return { redirectTo, otpResult }
	}else if(target === '5438976556'){
		// it's for guest user - google review
		const otpResult = { status: 'success', data: { success: true, data: { type: 'success', request_id: '346536'}}}

		return { redirectTo, otpResult }
	}
	else{
		// SEND OTP TO MOBILE
		const otpResult = await sendMsg91OTP(target)

		return { redirectTo, otpResult }
	}

}

export async function prepareResendOtpVerification({
    request,
	type,
	target,
	fdRedirectTo
}: {
	request: Request,
	type: VerificationTypes,
	target: string, // should be mobile no.
	fdRedirectTo?: string
}) {
	const verifyUrl = getRedirectToUrl({ request, type, target, redirectTo: fdRedirectTo})
	const redirectTo = new URL(verifyUrl.toString())

	if(target === '9876543211'){
		// it's for admin
		const otpResult = { status: 'success', data: { success: true, data: { type: 'success', request_id: '346526'}}}
	
        return { redirectTo, otpResult }
	}else if(target === '5438976556'){
		// it's for guest user - google review
		const otpResult = { status: 'success', data: { success: true, data: { type: 'success', request_id: '346536'}}}

		return { redirectTo, otpResult }
	}
	else{
		// SEND OTP TO MOBILE
		const otpResult = await resendMsg91OTP(target)

		return { redirectTo, otpResult }
	}

}

export type VerifyFunctionArgs = {
	request: Request
	submission: Submission<z.infer<typeof VerifySchema>>
	body: FormData | URLSearchParams
	user: {
		id: string;
		isOTPDone: boolean;
	} | null
}

export async function isCodeValid({
	code,
	type,
	target,
}: {
	code: string
	type: VerificationTypes
	target: string
}) {
	const verification = await prisma.verification.findUnique({
		where: {
			target_type: { target, type },
			OR: [{ expiresAt: { gt: new Date() } }, { expiresAt: null }],
		},
		select: { algorithm: true, secret: true, period: true, charSet: true },
	})
	if (!verification) return false

	// VERIFY OTP 
	const result = code === process.env.ADMIN_LOGIN_OTP ? true : false
	if (!result) return false

	return true
}

export async function isEmailCodeValid({
	code,
	type,
	target,
}: {
	code: string
	type: VerificationTypes
	target: string
}) {
	const verification = await prisma.verification.findUnique({
		where: {
			target_type: { target, type },
			OR: [{ expiresAt: { gt: new Date() } }, { expiresAt: null }],
		},
		select: { algorithm: true, secret: true, period: true, charSet: true },
	})

	if (!verification) return false
	const result = verifyTOTP({
		otp: code,
		...verification,
	})
	if (!result) return false

	return true
}

export async function isOtpCodeValid({
	code,
	type,
	target
}: {
	code: string
	type: VerificationTypes
	target: string
}) {
	if(target === '9876543211'){
		// it's for admin
		return code === process.env.ADMIN_LOGIN_OTP ? true : false
	}else if(target === '5438976556'){
        return code === '876560' ? true : false
	}
	else{
		const verifyResult = await verifyMsg91OTP(code, target)

		if(verifyResult.status === 'error') return false

		return true
	}
	
}

async function validateRequest(
	request: Request,
	body: URLSearchParams | FormData,
) {
	const deviceInfo = `${getSourceDeviceType(request)} - ${request.headers.get('user-agent')}`
	const submission = await parse(body, {
		schema: VerifySchema.superRefine(async (data, ctx) => {

			if(process.env.OTP_ENABLE === 'TRUE'){
				const otpIsValid = await isOtpCodeValid({
					code: data[codeQueryParam],
					type: data[typeQueryParam],
					target: data[targetQueryParam],
				})
				if(!otpIsValid) {
					ctx.addIssue({
						path: ['code'],
						code: z.ZodIssueCode.custom,
						message: `OTP is not valid.`
					})
					return 
				}
			}else{
				const codeIsValid = await isCodeValid({
					code: data[codeQueryParam],
					type: data[typeQueryParam],
					target: data[targetQueryParam],
				})
				if (!codeIsValid) {
					ctx.addIssue({
						path: ['code'],
						code: z.ZodIssueCode.custom,
						message: `OTP is not valid.`,
					})
					return
				}
			}
			
		}),
		async: true,
	})

	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	// this code path could be part of a loader (GET request), so we need to make
	// sure we're running on primary because we're about to make writes.
	//await ensurePrimary()

	const { value: submissionValue } = submission

	// CHECK IF USER EXIST, IF YES : create a auth session and send it to '/'
	const user = await prisma.user.findUnique({
		select: { id: true, isOTPDone: true, isGmailLoggedIn: true, gmailUserId: true, email: true },
		where: { mobileNo: submissionValue[targetQueryParam] }
	})

	const gmailUserId = await getGmailUserId(request)
		
	const gmailUser = gmailUserId
			? await prisma.gmailUser.findUnique({
				select: {
					id: true,
					name: true,
					givenName: true,
					email: true,
					picture: true,
				},
				where: { id: gmailUserId }

			})
			: null

	if(user && user.isOTPDone){

		if(gmailUserId && gmailUser && gmailUser.email && user.isGmailLoggedIn === false){
			// check if the gmailUserId linked, if yes then pass else check email match, if yes then linked with gmail

			if(user.email && user.email !== ''){
				// email is present
				if(user.email === gmailUser.email){
					// email should match for linked with gmail
					await prisma.user.update({
						select: { id: true, gmailUserId: true},
						data: {
							gmailUserId: gmailUser.id,
							isGmailLoggedIn: true,
							email: gmailUser.email
		
						},
						where: { id: user.id }
					})
				}
				
			}else {
				// means email is blank
				await prisma.user.update({
					select: { id: true, gmailUserId: true},
					data: {
						gmailUserId: gmailUser.id,
						isGmailLoggedIn: true,
						email: gmailUser.email
	
					},
					where: { id: user.id }
				})
			}
            
		}

		// set the header and send to '/'
		const session = await login({mobileNo: submissionValue[targetQueryParam], deviceInfo})
		const authSession = await authSessionStorage.getSession(
			request.headers.get('cookie'),
		)
		authSession.set(sessionKey, session.id)
		const verifySession = await verifySessionStorage.getSession()
		const headers = new Headers()
		headers.append(
			'set-cookie',
			await authSessionStorage.commitSession(authSession, {
				expires: session.expirationDate
			}),
		)
		headers.append(
			'set-cookie',
			await verifySessionStorage.destroySession(verifySession),
		)

		const isAdmin = await isAdminUserForRedirect(user.id, 'admin')

		if(isAdmin){
			return redirectWithToast(
				safeRedirect('/admin'),
				{
					type: 'success',
					description: 'Successfully Logged In. as Admin'
				},{
					headers,
				}
			)
		}
	    
		return redirectWithToast(
			safeRedirect(submissionValue[redirectToQueryParam] ?? '/'),
			{
				type: 'success',
				description: 'Successfully Logged In.'
			},{
				headers,
			}
		)
		// return redirect(safeRedirect("/"), {headers})
	}

	// async function deleteVerification() {
	// 	await prisma.verification.delete({
	// 		where: {
	// 			target_type: {
	// 				type: submissionValue[typeQueryParam],
	// 				target: submissionValue[targetQueryParam],
	// 			},
	// 		},
	// 	})
	// }

	if(user){
		switch (submissionValue[typeQueryParam]) {
			case 'onboarding': {
				//await deleteVerification()
				return handleOnboardingVerification({ request, body, submission, user })
			}
		}
	}else{
			const gmailUserId = await getGmailUserId(request)
		
			const gmailUser = gmailUserId
					? await prisma.gmailUser.findUnique({
						select: {
							id: true,
							name: true,
							email: true,
							givenName: true,
							picture: true,
						},
						where: { id: gmailUserId }
		
					})
					: null
		const newUser = await signupWithOTP({ mobileNo: submissionValue[targetQueryParam], name: gmailUser?.name, email: gmailUser?.email, deviceInfo: deviceInfo})

		if(!newUser) return null

		//slack notification for signup - otp done
		console.log('sending slack notification')
		await sendOTPDoneSlackMessage(submissionValue[targetQueryParam])
		// await webhook.send({
		// 	text: `User Otp Verified with phone no. ${submissionValue[targetQueryParam]}`,
		// })

		switch (submissionValue[typeQueryParam]) {
			case 'onboarding': {
				//await deleteVerification()
				return handleOnboardingVerification({ request, body, submission, user: newUser })
			}
		}
	}

	

}

export default function VerifyRoute() {
	const [searchParams] = useSearchParams()
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const parsedType = VerificationTypeSchema.safeParse(
		searchParams.get(typeQueryParam),
	)
	const type = parsedType.success ? parsedType.data : null

	const [sec, setSec] = useState(30)

	useEffect(() => {
		const interval = setInterval(() => {
			if(sec > 0){
				setSec(count => count - 1)
			}

			if(sec === 0){
				clearInterval(interval)
			}
		}, 1000)

		return () => {
			clearInterval(interval)
		}
	}, [sec])

	const checkEmail = (
		<>
			<h1 className="md:text-h1 text-h3">Let's upgrade your FDs</h1>
			<p className="mt-3 text-body-sm md:text-body-md text-muted-foreground">
			  Please enter your OTP
			</p>
		</>
	)

	const headings: Record<VerificationTypes, React.ReactNode> = {
		onboarding: checkEmail,
		'resend': checkEmail,
		'reset-password': checkEmail,
		'change-email': checkEmail,
		'verify-email': checkEmail,
		'2fa': (
			<>
				<h1 className="text-h1">Check your 2FA app</h1>
				<p className="mt-3 text-body-md text-muted-foreground">
					Please enter your 2FA code to verify your identity.
				</p>
			</>
		),
	}

	const [form, fields] = useForm({
		id: 'verify-form',
		constraint: getFieldsetConstraint(VerifySchema),
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			return parse(formData, { schema: VerifySchema })
		},
		shouldRevalidate: "onSubmit",
		defaultValue: {
			code: searchParams.get(codeQueryParam) ?? '',
			type,
			target: searchParams.get(targetQueryParam) ?? '',
			redirectTo: searchParams.get(redirectToQueryParam) ?? '',
		},
	})

	const [resendForm, resendFields] = useForm({
		id: 'resend-form',
		constraint: getFieldsetConstraint(VerifySchema),
		lastSubmission: actionData?.submission,
		// onValidate({ formData }) {
		// 	return parse(formData, { schema: VerifySchema })
		// },
		shouldRevalidate: "onSubmit",
		defaultValue: {
			code: searchParams.get(codeQueryParam) ?? '',
			type: 'resend',
			target: searchParams.get(targetQueryParam) ?? '',
			redirectTo: searchParams.get(redirectToQueryParam) ?? '',
		},
	})

	return (
		<main className="container flex flex-col justify-center pb-32 pt-20 px-0">
			<div className="text-center">
				{type ? headings[type] : 'Invalid Verification Type'}
			</div>

			<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
				<div>
					<ErrorList errors={form.errors} id={form.errorId} />
				</div>
				<div className="flex w-full gap-2">
					<Form method="POST" {...form.props} className="flex-1">
						<AuthenticityTokenInput />
						<HoneypotInputs />
						<Field
							labelProps={{
								htmlFor: fields[codeQueryParam].id,
								children: 'Enter OTP',
							}}
							inputProps={{
								...conform.input(fields[codeQueryParam]),
								autoComplete: 'one-time-code',
								autoFocus: true,
							}}
							errors={fields[codeQueryParam].errors}
						/>
						<input
							{...conform.input(fields[typeQueryParam], { type: 'hidden' })}
						/>
						<input
							{...conform.input(fields[targetQueryParam], { type: 'hidden' })}
						/>
						<input
							{...conform.input(fields[redirectToQueryParam], {
								type: 'hidden',
							})}
						/>
						
						<StatusButton
							className="w-full"
							status={isPending ? 'pending' : 'idle'}
							type="submit"
							disabled={isPending}
						>
							Submit
						</StatusButton>
					</Form>
           
				</div>

				<div className="flex w-full gap-2">
                    <Form method="POST" {...resendForm.props} className="flex-1">
							
							<input
							    {...conform.input(resendFields[typeQueryParam], { type: 'hidden' })}
							/>
							<input
								{...conform.input(resendFields[targetQueryParam], { type: 'hidden' })}
							/>
							<input
								{...conform.input(resendFields[redirectToQueryParam], {
									type: 'hidden',
								})}
							/>
							<StatusButton
								className="w-full mt-3"
								variant={"outline"}
								status={isPending ? 'pending' : actionData?.status ?? 'idle'}
								type="submit"
								disabled={sec !== 0}
							>
								{sec === 0 ? `Resend OTP` : `Resend OTP ( 00:${sec} )`} 
							</StatusButton>
					</Form>
				</div>
			</div>
		</main>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
